import { RouterStore } from '@superwf/mobx-react-router';
import { computed, observable, runInAction } from 'mobx';
import { parse, stringify } from 'qs';
import React from 'react';

import {
  extractHyperlink,
  hasModifierKey,
  langFromPath,
  pathnameWithoutLang,
  pathStartsWithLang,
  pathWithoutLang,
  prefixWithSlash,
  putLangIfLocal,
  putSearchIfMissing,
  requiresLocalNavigation,
  requiresSpinner,
  SearchParams,
  toPath,
  toPathname
} from '../util/NavigationUtil';
import GaDataStore from './GaDataStore';
import { ALLOWED_LANGUAGES, DEFAULT_LANGUAGE, Language, languageSlug } from './LanguageStore';
import { RootStore } from './RootStore';
import { SEGMENT_ARI, SEGMENT_BUSINESS, SEGMENT_ERA } from './SegmentStore';

export default class RouteStore {
  @observable accessor showingSpinner: boolean = false;

  private routing: RouterStore;
  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.routing = rootStore.routing;
    this.rootStore = rootStore;

    window.telia = {
      ...window.telia,
      navigateLocal: this.navigateLocal,
      navigate: this.navigate
    };
  }

  @computed
  get pathname() {
    return this.location.pathname || '/';
  }

  @computed
  get search() {
    return this.location.search;
  }

  @computed
  get path() {
    return toPath(this.pathname);
  }

  @computed
  get decodedParams() {
    return parse(this.location.search, { ignoreQueryPrefix: true }) as any;
  }

  @computed
  get hash() {
    return this.location.hash;
  }

  public showSpinner = () => {
    runInAction(() => {
      this.showingSpinner = true;
    });
  };

  public hideSpinner = () => {
    runInAction(() => {
      this.showingSpinner = false;
    });
  };

  public currentPathStartsWith(url: string) {
    return this.pathnameWithoutLang.startsWith(pathnameWithoutLang(url));
  }

  public navigate = (event: React.MouseEvent<HTMLElement>) => {
    if (hasModifierKey(event) || event.currentTarget?.target === '_blank') {
      return;
    }
    if (requiresLocalNavigation(event.currentTarget)) {
      this.navigateLocal(event);
    } else {
      runInAction(() => {
        this.showingSpinner = requiresSpinner(event.currentTarget, this.rootStore.initialState?.isMyTeliaUserAgent);
      });
      const url = event.currentTarget?.href;
      if (url) {
        event.preventDefault();
        return window.location.assign(url);
      }
    }
  };

  private removeGaFromSearchParams(search: string) {
    const searchWithoutGa = parse(search, { ignoreQueryPrefix: true });
    delete searchWithoutGa._ga;
    return stringify(searchWithoutGa, { addQueryPrefix: true });
  }

  private navigateLocal = (event: React.MouseEvent<HTMLElement>) => {
    runInAction(() => {
      this.showingSpinner = false;
    });

    const target = event.currentTarget;
    if (!this.isOnlyHashChange(target)) {
      event.preventDefault();
      GaDataStore.pushWithoutCmsData({ event: 'react-pageview' });
    }
    if (!target.pathname && !target.search && !target.hash) {
      const hyperLink = extractHyperlink(event.currentTarget.href);
      if (hyperLink.pathname) {
        runInAction(() => {
          this.routing.push(prefixWithSlash(hyperLink.pathname) + this.removeGaFromSearchParams(hyperLink.search) + hyperLink.hash);
        });
        return;
      }
    }
    const targetStartWithLanguage: boolean = target.pathname.startsWith('/ru/') || target.pathname.startsWith('/en/');
    const language = !!this.langFromCurrentPath && !targetStartWithLanguage ? `/${this.langFromCurrentPath}` : '';
    runInAction(() => {
      this.routing.push(language + target.pathname + this.removeGaFromSearchParams(target.search) + target.hash);
      // move focus to new page content wrapper after navigation to ensure consistent focus management for screen reader users
      document.getElementById('main-content')?.focus();
    });
  };

  @computed
  get langFromCurrentPath(): Language {
    return langFromPath(this.path);
  }

  @computed
  get pathWithoutLang(): string[] {
    return pathWithoutLang(this.path);
  }

  @computed
  get currentHomepagePathname() {
    const homepageSegment = this.rootStore.segmentStore.currentSegment === SEGMENT_BUSINESS ? SEGMENT_ARI : SEGMENT_ERA;
    return `${languageSlug(this.langFromCurrentPath)}/${homepageSegment}`;
  }

  @computed
  get currentPathIsHomepage(): boolean {
    return ['/', this.currentHomepagePathname].includes(this.pathname);
  }

  @computed
  private get pathnameWithoutLang() {
    return toPathname(this.pathWithoutLang);
  }

  public setLanguage = (requiredLang: Language) => {
    if (!ALLOWED_LANGUAGES.includes(requiredLang)) {
      throw new Error(`Unsupported language: ${requiredLang}`);
    }
    runInAction(() => {
      const currentLang = this.langFromCurrentPath;
      const currentPathMatchesLang = requiredLang === DEFAULT_LANGUAGE ? !pathStartsWithLang(this.path) : currentLang === requiredLang;
      !currentPathMatchesLang && this.routing.push(languageSlug(requiredLang) + this.pathnameWithoutLang + location.search + location.hash);
    });
  };

  @computed
  private get location() {
    return this.routing.location;
  }

  public push = (url: string, search: SearchParams = null) => {
    const link = extractHyperlink(url);
    putSearchIfMissing(link, search);
    GaDataStore.pushWithoutCmsData({ event: 'react-pageview' });
    this.navigateLocalOrExternal(
      link,
      (actualUrl: string) =>
        runInAction(() => {
          this.routing.push(actualUrl);
        }),
      (actualUrl: string) => window.location.assign(actualUrl)
    );
  };

  public replace = (url: string) => {
    GaDataStore.pushWithoutCmsData({ event: 'react-pageview' });
    this.navigateLocalOrExternal(
      extractHyperlink(url),
      (actualUrl: string) => this.routing.replace(actualUrl),
      (actualUrl: string) => window.location.replace(actualUrl)
    );
  };

  private navigateLocalOrExternal = (link: HTMLHyperlinkElementUtils, navigateLocal: (url: string) => void, navigateExternal: (url: string) => void) => {
    const target = new EventTarget();
    target.href = link.href;
    if (requiresLocalNavigation(target)) {
      if (!pathStartsWithLang(toPath(link.pathname)) && this.langFromCurrentPath) {
        putLangIfLocal(link, this.langFromCurrentPath);
      }
      navigateLocal(prefixWithSlash(link.pathname) + link.search + link.hash);
    } else {
      runInAction(() => {
        this.showingSpinner = requiresSpinner(link, this.rootStore.initialState?.isMyTeliaUserAgent);
      });
      navigateExternal(link.href);
    }
  };

  private isOnlyHashChange = (target: EventTarget): boolean => {
    return this.pathname === target.pathname && this.location.search === target.search;
  };
}
